import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, OnDestroy {
  profileForm!: FormGroup;
  isLoading = true;
  currentUser: any = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private globalStateService: GlobalStateService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadUserProfile();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeForm(): void {
    this.profileForm = this.fb.group({
      title: ['Mr.', [Validators.required]],
      name: ['', [Validators.required, Validators.minLength(2)]],
      login_id: [{ value: '', disabled: true }, [Validators.required]],
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
      password: [''],
      confirm_password: ['']
    }, { validators: this.passwordMatchValidator });
  }

  private passwordMatchValidator(formGroup: FormGroup): { [key: string]: boolean } | null {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirm_password')?.value;

    // Only validate if password is entered
    if (password && password !== confirmPassword) {
      formGroup.get('confirm_password')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    // Clear the error if passwords match
    const confirmPasswordControl = formGroup.get('confirm_password');
    if (confirmPasswordControl?.hasError('passwordMismatch')) {
      confirmPasswordControl.setErrors(null);
    }

    return null;
  }

  private loadUserProfile(): void {
    // Subscribe to global state user
    this.subscriptions.push(
      this.globalStateService.user.subscribe({
        next: (user) => {
          if (user) {
            this.currentUser = user;
            this.patchFormValues(user);
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error('Error loading user profile:', error);
          this.isLoading = false;
        }
      })
    );
  }

  private patchFormValues(user: any): void {
    this.profileForm.patchValue({
      title: user.title || 'Mr.',
      name: user.name || '',
      login_id: user.login_id || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '',
      confirm_password: ''
    });
  }

  onSubmit(): void {
    if (this.profileForm.invalid) {
      this.markFormGroupTouched(this.profileForm);
      Swal.fire({
        text: "Please fill in all required fields correctly!",
        icon: "error",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
      return;
    }

    this.isLoading = true;
    const formValue = this.profileForm.getRawValue(); // Get raw value to include disabled fields

    console.log('Profile Form Data:', formValue);
    console.log('Form Status:', {
      valid: this.profileForm.valid,
      touched: this.profileForm.touched,
      dirty: this.profileForm.dirty,
      values: formValue
    });

    const submitData: any = {
      title: formValue.title,
      name: formValue.name,
      phone: formValue.phone
    };

    // Only include password if it's provided
    if (formValue.password && formValue.password.trim() !== '') {
      submitData.password = formValue.password;
    }

    console.log('Submit Data:', submitData);

    this.subscriptions.push(
      this.authService.UpdateProfile(submitData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          console.log('Profile updated successfully:', response);

          Swal.fire({
            text: "Profile has been successfully updated!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });

          // Update global state with new user data
          if (response.data) {
            this.globalStateService.user.next(response.data);
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error updating profile:', error);

          Swal.fire({
            text: error.error?.message || "Failed to update profile. Please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string): boolean {
    const field = this.profileForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.profileForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['email']) return 'Invalid email format';
      if (field.errors['minlength']) return `Minimum length is ${field.errors['minlength'].requiredLength}`;
      if (field.errors['passwordMismatch']) return 'Passwords do not match';
    }
    return '';
  }

  hasPasswordMismatch(): boolean {
    const password = this.profileForm.get('password')?.value;
    const confirmPassword = this.profileForm.get('confirm_password');
    return !!(password && confirmPassword?.hasError('passwordMismatch') && confirmPassword?.touched);
  }

  resetForm(): void {
    if (this.currentUser) {
      this.patchFormValues(this.currentUser);
      this.profileForm.markAsPristine();
      this.profileForm.markAsUntouched();
    }
  }
}
